import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { invoiceNumberLabel, hasDisplayNumber } from '../utils/invoiceDisplay';
import { useAuth } from '../contexts/AuthContext';

/**
 * Bidder-facing, READ-ONLY invoice detail (Feature 1). Deliberately separate
 * from the admin InvoiceDetail (which has Mark-Paid / photo-upload actions).
 * A bidder can only load their own invoice — the owner-scoped Firestore read
 * rule denies invoices whose `uid` !== the signed-in user.
 */

interface BidderInvoice {
  /** DOC ID, not a label. */
  invoiceNumber: string;
  /** The human, buyer-facing number (8001…). Absent until backfilled. */
  displayInvoiceNumber?: number;
  auctionTitle: string;
  uid?: string;
  bidderNumber: string;
  bidder: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  items: Array<{ lotNumber: string; sku: string; title: string; quantity: number; highBid: number }>;
  subtotal: number;
  buyersPremium: number;
  buyersPremiumRate: number;
  salesTax: number;
  salesTaxRate: number;
  total: number;
  status: 'unpaid' | 'paid' | 'partial' | 'picked_up';
  createdAt: string;
  paidAt?: string;
  paymentLink: string;
}

const MyInvoiceDetail: React.FC = () => {
  const { invoiceNumber } = useParams<{ invoiceNumber: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [invoice, setInvoice] = useState<BidderInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!invoiceNumber) return;
      try {
        const snap = await getDoc(doc(db, 'invoices', invoiceNumber));
        if (!active) return;
        if (snap.exists()) {
          const data = snap.data() as BidderInvoice;
          // Defensive: the rule already blocks cross-user reads, but guard the UI too.
          if (currentUser && data.uid && data.uid !== currentUser.uid) {
            setDenied(true);
          } else {
            setInvoice(data);
          }
        }
      } catch (e) {
        console.error('Failed to load invoice:', e);
        if (active) setDenied(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [invoiceNumber, currentUser]);

  if (loading) return <div className="max-w-3xl mx-auto p-6 text-center text-gray-500">Loading invoice…</div>;
  if (denied) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center">
        <p className="text-gray-600">This invoice isn't available to your account.</p>
        <button onClick={() => navigate('/my-invoices')} className="mt-4 text-blue-600 hover:text-blue-800">
          Back to my invoices
        </button>
      </div>
    );
  }
  if (!invoice) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center">
        <p className="text-gray-600">Invoice not found.</p>
        <button onClick={() => navigate('/my-invoices')} className="mt-4 text-blue-600 hover:text-blue-800">
          Back to my invoices
        </button>
      </div>
    );
  }

  const isPaid = invoice.status === 'paid' || !!invoice.paidAt;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <button onClick={() => navigate('/my-invoices')} className="text-blue-600 hover:text-blue-800">
        ← Back to my invoices
      </button>

      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8 border-b-2 border-gray-300 pb-6">
          <h1 className="text-3xl font-bold text-gray-900">Schmidt Auctions</h1>
          <p className="text-gray-600">Invoice</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div>
            <h2 className="text-lg font-bold mb-2">Bill To:</h2>
            <p className="font-semibold">{invoice.bidder.firstName} {invoice.bidder.lastName}</p>
            <p className="text-sm">Bidder #{invoice.bidderNumber}</p>
            {invoice.bidder.address && <p className="text-sm">{invoice.bidder.address}</p>}
            {invoice.bidder.city && (
              <p className="text-sm">{invoice.bidder.city}, {invoice.bidder.state} {invoice.bidder.zip}</p>
            )}
            <p className="text-sm mt-2">{invoice.bidder.email}</p>
            <p className="text-sm">{invoice.bidder.phone}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Invoice Number</p>
            <p className="text-2xl font-bold tracking-tight text-gray-900">
              {invoiceNumberLabel(invoice)}
            </p>
            {hasDisplayNumber(invoice) && (
              <p className="text-[10px] text-gray-400 font-mono break-all leading-tight mb-3">
                ref {invoice.invoiceNumber}
              </p>
            )}
            {!hasDisplayNumber(invoice) && <div className="mb-3" />}
            <p className="text-sm text-gray-600">Sale</p>
            <p className="font-semibold mb-3">{invoice.auctionTitle}</p>
            <p className="text-sm text-gray-600">Date</p>
            <p className="mb-3">{new Date(invoice.createdAt).toLocaleDateString()}</p>
            <span
              className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                isPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {isPaid ? 'Paid' : 'Unpaid'}
            </span>
          </div>
        </div>

        <table className="w-full mb-8">
          <thead>
            <tr className="border-b-2 border-gray-300">
              <th className="text-left py-2">Lot</th>
              <th className="text-left py-2">SKU</th>
              <th className="text-left py-2">Description</th>
              <th className="text-center py-2">Qty</th>
              <th className="text-right py-2">Price</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, i) => (
              <tr key={i} className="border-b border-gray-200">
                <td className="py-3">{item.lotNumber}</td>
                <td className="py-3 text-sm text-gray-600">{item.sku}</td>
                <td className="py-3">{item.title}</td>
                <td className="py-3 text-center">{item.quantity}</td>
                <td className="py-3 text-right">${(item.highBid ?? 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className="w-64">
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-semibold">${(invoice.subtotal ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Buyer's Premium ({((invoice.buyersPremiumRate ?? 0) * 100).toFixed(0)}%):</span>
              <span className="font-semibold">${(invoice.buyersPremium ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-300">
              <span className="text-gray-600">Sales Tax ({((invoice.salesTaxRate ?? 0) * 100).toFixed(1)}%):</span>
              <span className="font-semibold">${(invoice.salesTax ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-3 border-b-2 border-gray-900">
              <span className="text-lg font-bold">Total:</span>
              <span className="text-lg font-bold text-green-600">${(invoice.total ?? 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {invoice.paymentLink && !isPaid && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-700 mb-2">Pay online:</p>
            <a
              href={invoice.paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 text-sm break-all"
            >
              {invoice.paymentLink}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyInvoiceDetail;
